import {useSiteLanguage} from "../career/SiteLanguage";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BriefcaseBusiness, CalendarDays, MapPin, Search, SlidersHorizontal } from "lucide-react";
import "../styles/JobList.css";
import { collectionValues, resolveMediaUrl } from "../utils/mediaUrl";
import {useCareerLanguage} from "../career/CareerLanguage";
import {catalogLabel} from "../career/CareerCatalog";
import {jobLabel,jobLocation,jobDeadline} from '../utils/jobPresentation';

const ALL_COUNTRIES = "Tüm Ülkeler";
const ALL_CITIES = "Tüm Şehirler";
const ALL_DISTRICTS = "Tüm İlçeler";
const ALL_POSITIONS = "Tüm Pozisyonlar";

const uniqueValues = (jobs, field) => [
  ...new Set(jobs.map((job) => jobLabel(job[field])).filter(Boolean)),
].sort((a, b) => a.localeCompare(b, "tr"));

const JobList = () => {
  const {tx} = useSiteLanguage();
  const {t,lang}=useCareerLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(ALL_COUNTRIES);
  const [selectedCity, setSelectedCity] = useState(ALL_CITIES);
  const [selectedPosition, setSelectedPosition] = useState(ALL_POSITIONS);
  const [selectedDistrict, setSelectedDistrict] = useState(ALL_DISTRICTS);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ilan/getirhepsi`)
      .then((response) => {
        if (!response.ok) throw new Error("İlanlar alınamadı.");
        return response.json();
      })
      .then((data) => {
        // Kariyer sitesinde yalnızca aktif ilanlar gösterilir.
        setJobs(collectionValues(data).filter((job) => job.status === true));
      })
      .catch((requestError) => setError(requestError.message || "İlanlar alınamadı."))
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => uniqueValues(jobs, "ilanUlkelerAdi"), [jobs]);
  const cities = useMemo(() => uniqueValues(jobs, "ilanillerAdi"), [jobs]);
  const positions = useMemo(() => uniqueValues(jobs, "ilanPozisyonAdi"), [jobs]);
  const districts = useMemo(() => {
    const cityJobs = selectedCity === ALL_CITIES
      ? jobs
      : jobs.filter((job) => jobLabel(job.ilanillerAdi) === selectedCity);
    return [...new Set(cityJobs.map((job) => jobLabel(job.ilanIlceAdi)).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [jobs, selectedCity]);

  const filteredJobs = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase(lang);
    return jobs.filter((job) => {
      if (selectedCountry !== ALL_COUNTRIES && jobLabel(job.ilanUlkelerAdi) !== selectedCountry) return false;
      if (selectedCity !== ALL_CITIES && jobLabel(job.ilanillerAdi) !== selectedCity) return false;
      if (selectedDistrict !== ALL_DISTRICTS && jobLabel(job.ilanIlceAdi) !== selectedDistrict) return false;
      if (selectedPosition !== ALL_POSITIONS && jobLabel(job.ilanPozisyonAdi) !== selectedPosition) return false;
      if (!query) return true;

      return [
        job.ilanBaslik,
        job.kisaAciklamasi,
        job.magazaAdi,
        job.ilanPozisyonAdi,
        job.ilanillerAdi,
        job.ilanIlceAdi,
        job.ilanUlkelerAdi,
        catalogLabel('country',job.ilanUlkelerAdi,lang),
        catalogLabel('position',job.ilanPozisyonAdi,lang),
      ].filter(Boolean).join(" ").toLocaleLowerCase(lang).includes(query);
    });
  }, [jobs, searchText, selectedCountry, selectedCity, selectedDistrict, selectedPosition,lang]);

  useEffect(() => setCurrentPage(1), [searchText, selectedCountry, selectedCity, selectedDistrict, selectedPosition]);

  const pageCount = Math.ceil(filteredJobs.length / itemsPerPage);
  const currentJobs = filteredJobs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const clearFilters = () => {
    setSearchText("");
    setSelectedCountry(ALL_COUNTRIES);
    setSelectedCity(ALL_CITIES);
    setSelectedDistrict(ALL_DISTRICTS);
    setSelectedPosition(ALL_POSITIONS);
  };

  const handleDetail = async (jobId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Ilan/goruntule/${jobId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch (requestError) {
      console.error("Görüntüleme bildirimi başarısız:", requestError);
    } finally {
      navigate(`/ilan/${jobId}`);
    }
  };

  if (loading) return <div className="jobs-state">{t('loading')}</div>;

  return (
    <main className="jobs-page">
      <section className="jobs-hero">
        <div className="jobs-hero-copy">
          <span>FULLA KARIYER</span>
          <h1>{t('findJob')}</h1>
          <p>{t('jobs')}</p>
        </div>
        <div className="jobs-hero-count">
          <BriefcaseBusiness size={23} />
          <strong>{jobs.length}</strong>
          <span>{t('jobs')}</span>
        </div>
        <label className="jobs-search">
          <Search size={20} />
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder={t('jobSearch')}
          />
        </label>
      </section>

      {error && <div className="jobs-error-banner">{t('error')}</div>}

      <section className="jobs-layout">
        <aside className="jobs-filter-panel">
          <div className="jobs-filter-title">
            <SlidersHorizontal size={18} />
            <div><strong>{t('filters')}</strong><span>{t('findJob')}</span></div>
          </div>

          <label>{t('residence')}
            <select value={selectedCountry} onChange={(event) => setSelectedCountry(event.target.value)}>
              <option value={ALL_COUNTRIES}>{t('all')}</option>
              {countries.map((value) => <option key={value} value={value}>{catalogLabel('country',value,lang)}</option>)}
            </select>
          </label>
          <label>{t('city')}
            <select value={selectedCity} onChange={(event) => {
              setSelectedCity(event.target.value);
              setSelectedDistrict(ALL_DISTRICTS);
            }}>
              <option value={ALL_CITIES}>{t('all')}</option>
              {cities.map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
          <label>{t('address')}
            <select value={selectedDistrict} onChange={(event) => setSelectedDistrict(event.target.value)}>
              <option value={ALL_DISTRICTS}>{t('all')}</option>
              {districts.map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
          <label>{t('position')}
            <select value={selectedPosition} onChange={(event) => setSelectedPosition(event.target.value)}>
              <option value={ALL_POSITIONS}>{t('all')}</option>
              {positions.map((value) => <option key={value} value={value}>{catalogLabel('position',value,lang)}</option>)}
            </select>
          </label>
          <button type="button" className="jobs-clear-filter" onClick={clearFilters}>{t('clear')}</button>
        </aside>

        <div className="jobs-results">
          <div className="jobs-results-heading">
            <div><strong>{t('jobs')}</strong><span>{filteredJobs.length}</span></div>
          </div>

          {currentJobs.length ? (
            <div className="jobs-grid">
              {currentJobs.map((job) => (
                <article className="modern-job-card" key={job.ilanId}>
                  <div className="modern-job-cover">
                    {job.ilanFotoUrl ? (
                      <img
                        src={resolveMediaUrl(job.ilanFotoUrl)}
                        alt={job.ilanBaslik}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                          event.currentTarget.nextElementSibling?.classList.add("is-visible");
                        }}
                      />
                    ) : null}
                    <div className={`modern-job-fallback ${job.ilanFotoUrl ? "" : "is-visible"}`}>FULLA</div>
                    {jobLabel(job.ilanPozisyonAdi)&&<span className="modern-job-position">{catalogLabel('position',jobLabel(job.ilanPozisyonAdi),lang)}</span>}
                  </div>
                  <div className="modern-job-body">
                    <small>{jobLabel(job.magazaAdi) || "Fulla Moda"}</small>
                    <h2>{job.ilanBaslik}</h2>
                    <p>{jobLabel(job.kisaAciklamasi) || t('inspect')}</p>
                    {(jobLocation(job,lang)||jobDeadline(job.gecerlilikTarihi,lang))&&<div className="modern-job-meta">
                      {jobLocation(job,lang)&&<span><MapPin size={15} aria-hidden="true"/> {jobLocation(job,lang)}</span>}
                      {jobDeadline(job.gecerlilikTarihi,lang) && (
                        <span><CalendarDays size={15} aria-hidden="true"/> {t('deadline')} {jobDeadline(job.gecerlilikTarihi,lang)}</span>
                      )}
                    </div>}
                    <button type="button" onClick={() => handleDetail(job.ilanId)}>{t('inspect')} <span>→</span></button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="jobs-empty">
              <Search size={34} />
              <strong>{t('emptyJobs')}</strong>
              <button type="button" onClick={clearFilters}>{t('all')}</button>
            </div>
          )}

          {pageCount > 1 && (
            <nav className="jobs-pagination" aria-label={tx("İlan sayfaları")}>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={currentPage === page ? "is-active" : ""}
                >
                  {page}
                </button>
              ))}
            </nav>
          )}
        </div>
      </section>
    </main>
  );
};

export default JobList;
